import type { ComponentType, ReactNode } from "react";
import {
  Box,
  CardActionArea,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { CCS_PALETTE } from "./theme";

export interface StatCardSubItem {
  label: string;
  value: number | string;
  color?: "default" | "success" | "warning" | "error" | "info";
}

export interface StatCardProps {
  label: string;
  value: number | string;
  subItems?: StatCardSubItem[];
  loading?: boolean;
  description?: string;
  icon?: ComponentType<{ sx?: object }>;
  iconColor?: string;
  href?: string;
  onClick?: () => void;
  highlight?: boolean;
}

const subItemColor: Record<NonNullable<StatCardSubItem["color"]>, string> = {
  default: "text.secondary",
  success: "success.main",
  warning: "warning.main",
  error: "error.main",
  info: "info.main",
};

function StatCardInner({
  label,
  value,
  subItems,
  loading,
  description,
  icon: Icon,
  iconColor,
  highlight,
}: Omit<StatCardProps, "href" | "onClick">) {
  const accent = iconColor ?? CCS_PALETTE.katanaPurple;
  const warnHighlight = highlight && !iconColor;

  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 110,
      }}
    >
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}
      >
        <Typography
          variant="overline"
          color={warnHighlight ? "warning.dark" : "text.secondary"}
          sx={{ lineHeight: 1.4 }}
        >
          {label}
        </Typography>
        {Icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: warnHighlight
                ? "warning.lighter"
                : `${accent}18`,
              flexShrink: 0,
            }}
          >
            <Icon
              sx={{
                fontSize: 20,
                color: warnHighlight ? "warning.dark" : accent,
              }}
            />
          </Box>
        )}
      </Stack>

      {loading ? (
        <CircularProgress size={24} sx={{ my: 0.5 }} />
      ) : (
        <Typography
          variant="h4"
          color={warnHighlight ? "warning.dark" : "text.primary"}
          sx={{ fontWeight: 700 }}
        >
          {value}
        </Typography>
      )}

      {description && !loading ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, mb: subItems ? 1 : 0 }}
        >
          {description}
        </Typography>
      ) : null}

      {subItems && subItems.length > 0 && !loading ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: "auto", pt: 1.5, flexWrap: "wrap", rowGap: 0.5 }}
        >
          {subItems.map((item) => (
            <Box key={item.label}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: item.color ? subItemColor[item.color] : "text.primary",
                }}
              >
                {item.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

/**
 * Stat card — Minimal UI Kit card pattern.
 * Supports an optional icon in the top-right corner with a tinted background,
 * optional click/href to make the card navigable, and a highlight mode for
 * attention-grabbing metrics (e.g. pending items > 0).
 */
export function StatCard(props: StatCardProps) {
  const { href, onClick, highlight, ...rest } = props;

  const paper = (children: ReactNode) => (
    <Paper
      elevation={2}
      sx={{
        height: "100%",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "background.paper",
        ...(highlight && {
          border: "1px solid",
          borderColor: "warning.light",
          bgcolor: "warning.lighter",
        }),
      }}
    >
      {children}
    </Paper>
  );

  if (href) {
    return paper(
      <CardActionArea
        component="a"
        href={href}
        sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
      >
        <StatCardInner {...rest} highlight={highlight} />
      </CardActionArea>,
    );
  }

  if (onClick) {
    return paper(
      <CardActionArea
        onClick={onClick}
        sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
      >
        <StatCardInner {...rest} highlight={highlight} />
      </CardActionArea>,
    );
  }

  return paper(<StatCardInner {...rest} highlight={highlight} />);
}
